import { eventChannel } from 'redux-saga';
import { call, put, take, race, takeLatest, all, fork } from 'redux-saga/effects';
import {
    query,
    orderByChild,
    startAt,
    endAt,
    endBefore,
    ref as rtdbRef,
    limitToLast,
    onValue,
    get,
    limitToFirst
} from 'firebase/database';
import { cancelled, cancel, select } from 'redux-saga/effects';
import {
    updateAccessLog,
    appendAccessLog,
    prependAccessLog
} from 'store/actions/AccessLog';
import { FETCH_MORE_LOGS, LOGOUT_USER } from 'store/actions/types';
import { _accessLog, _activeOrg } from './Selectors';
import { rtdb } from 'config/firebase';

export function createLiveAccessLogChannel(orgId, pageSize = 20) {
    return eventChannel(emit => {
        const baseRef = rtdbRef(rtdb, `orgs/${orgId}/access_log`);
        const q = query(baseRef, orderByChild('access_time'), limitToLast(pageSize));

        const unsubscribe = onValue(q, snapshot => {
            const results = [];

            snapshot.forEach(childSnap => {
                const log = { logItemId: childSnap.key, ...childSnap.val() };
                results.push(log);
            });

            results.sort((a, b) => b.access_time.localeCompare(a.access_time));
            emit(results);
        });

        return () => unsubscribe();
    });
}

function* watchLiveAccessLogs(orgId) {
    const channel = yield call(createLiveAccessLogChannel, orgId);

    try {
        while (true) {
            const newLogs = yield take(channel);
            const currentLogs = yield select(_accessLog);

            if (newLogs.length === 0) continue;

            const newestTime = currentLogs.length ? currentLogs[0].access_time : null;
            const freshLogs = newestTime
                ? newLogs.filter(log => log.access_time > newestTime)
                : newLogs;

            if (freshLogs.length > 0) {
                yield put(prependAccessLog(freshLogs));
            }
        }
    } finally {
        if (yield cancelled()) {
            channel.close();
        }
    }
}

let liveTask = null;

export function* fetchPaginatedLogsWithFilters({ payload }) {
    const { lastAccessTime, filters, orgId } = payload;
    const pageSize = 20;
    const isAdvanced = !!filters.dateFrom;

    const currentRef = rtdbRef(rtdb, `orgs/${orgId}/access_log`);
    const historyRef = rtdbRef(rtdb, `history/orgs/${orgId}`);

    if (isAdvanced) {
        const dateFromISO = filters.dateFrom ? filters.dateFrom.toISOString() : null;
        const dateToISO = filters.dateTo ? filters.dateTo.toISOString() : null;

        const oldestSnap = yield call(
            get,
            query(currentRef, orderByChild('access_time'), limitToFirst(1))
        );
        let oldestCurrent = null;
        if (oldestSnap.exists()) {
            oldestSnap.forEach(child => {
                oldestCurrent = child.val().access_time;
            });
        }

        const logsAccumulator = [];

        if (oldestCurrent && (!dateToISO || dateToISO >= oldestCurrent)) {
            const windowStartCurrent =
                dateFromISO && dateFromISO > oldestCurrent ? dateFromISO : oldestCurrent;
            let currentQuery = query(currentRef, orderByChild('access_time'));
            if (windowStartCurrent) {
                currentQuery = query(currentQuery, startAt(windowStartCurrent));
            }
            if (dateToISO) {
                currentQuery = query(currentQuery, endAt(dateToISO));
            }
            const currentSnap = yield call(get, currentQuery);
            if (currentSnap.exists()) {
                currentSnap.forEach(child => {
                    logsAccumulator.push({
                        logItemId: child.key,
                        ...child.val()
                    });
                });
            }

            if (dateFromISO) {
                let histQuery = query(
                    historyRef,
                    orderByChild('access_time'),
                    endAt(oldestCurrent)
                );
                histQuery = query(histQuery, startAt(dateFromISO));
                const histSnap = yield call(get, histQuery);
                if (histSnap.exists()) {
                    histSnap.forEach(child => {
                        logsAccumulator.push({
                            logItemId: child.key,
                            ...child.val()
                        });
                    });
                }
            }
        } else {
            let histQuery = query(historyRef, orderByChild('access_time'));
            if (dateFromISO) {
                histQuery = query(histQuery, startAt(dateFromISO));
            }
            if (dateToISO) {
                histQuery = query(histQuery, endAt(dateToISO));
            }
            const histSnap = yield call(get, histQuery);
            if (histSnap.exists()) {
                histSnap.forEach(child => {
                    logsAccumulator.push({
                        logItemId: child.key,
                        ...child.val()
                    });
                });
            }
        }

        const fullyFiltered = logsAccumulator.filter(log => {
            if (filters.access_type && log.access_type !== filters.access_type) {
                return false;
            }
            if (filters.consumer_id && log.consumer_id !== filters.consumer_id) {
                return false;
            }
            if (filters.property_id && log.property_id !== filters.property_id) {
                return false;
            }
            if (filters.success !== '' && log.success !== filters.success) {
                return false;
            }
            return true;
        });

        fullyFiltered.sort((a, b) => b.access_time.localeCompare(a.access_time));

        yield put(updateAccessLog(fullyFiltered));
        return;
    }

    if (!isAdvanced) {
        const q = query(
            rtdbRef(rtdb, `orgs/${orgId}/access_log`),
            orderByChild('access_time'),
            ...(lastAccessTime ? [endBefore(lastAccessTime)] : []),
            limitToLast(pageSize)
        );

        const snap = yield call(get, q);
        const nextCurrentLogs = [];

        if (snap.exists()) {
            snap.forEach(child => {
                nextCurrentLogs.push({ logItemId: child.key, ...child.val() });
            });
        }

        nextCurrentLogs.sort((a, b) => b.access_time.localeCompare(a.access_time));

        if (nextCurrentLogs.length === pageSize) {
            if (lastAccessTime === null) {
                yield put(updateAccessLog(nextCurrentLogs));
            } else {
                yield put(appendAccessLog(nextCurrentLogs));
            }
            return;
        }

        const missing = pageSize - nextCurrentLogs.length;
        const boundary =
            lastAccessTime ||
            (nextCurrentLogs.length > 0
                ? nextCurrentLogs[nextCurrentLogs.length - 1].access_time
                : null);

        let histLogs = [];

        if (boundary) {
            const histQuery = query(
                historyRef,
                orderByChild('access_time'),
                endBefore(boundary),
                limitToLast(missing)
            );
            const histSnap = yield call(get, histQuery);
            if (histSnap.exists()) {
                histSnap.forEach(child => {
                    histLogs.push({ logItemId: child.key, ...child.val() });
                });
            }
        } else {
            const histQuery = query(
                historyRef,
                orderByChild('access_time'),
                limitToLast(pageSize)
            );
            const histSnap = yield call(get, histQuery);
            if (histSnap.exists()) {
                histSnap.forEach(child => {
                    histLogs.push({ logItemId: child.key, ...child.val() });
                });
            }
        }

        histLogs.sort((a, b) => b.access_time.localeCompare(a.access_time));

        if (nextCurrentLogs.length === 0 && histLogs.length === 0) {
            yield put(appendAccessLog([]));
            return;
        }

        const combinedLogs = [...nextCurrentLogs, ...histLogs];
        if (lastAccessTime === null) {
            yield put(updateAccessLog(combinedLogs));
        } else {
            yield put(appendAccessLog(combinedLogs));
        }
        return;
    }
}

function* watchSearchMode() {
    while (true) {
        const {
            payload: { filters, orgId }
        } = yield take(FETCH_MORE_LOGS);

        if (!filters.dateFrom) {
            if (!liveTask) {
                liveTask = yield fork(watchLiveAccessLogs, orgId);
            }
        } else {
            if (liveTask) {
                yield cancel(liveTask);
                liveTask = null;
            }
        }
    }
}

function* watchLogoutCleanup() {
    yield take(LOGOUT_USER);
    if (liveTask) {
        yield cancel(liveTask);
        liveTask = null;
    }
}

export function* watchPaginatedAccessLog() {
    yield takeLatest(FETCH_MORE_LOGS, fetchPaginatedLogsWithFilters);
}

export default function* rootSaga() {
    yield all([
        fork(watchPaginatedAccessLog),
        fork(watchSearchMode),
        fork(watchLogoutCleanup)
    ]);
}
